<template>
	<v-row>
		<v-col>
			<v-card>
				<v-card-title
					class="d-flex align-center"
				>
					<v-icon
						icon="$invoice"
						start
					/>
					<translate>Invoices</translate>
				</v-card-title>
				<v-card-text>
					<v-row>
						<v-col cols="4">
							<v-text-field
								v-model="identifierFilter"
								:label="$gettext('Search by invoice number')"
								single-line
								hide-details
								variant="outlined"
								@keyup.enter="updateFilter"
							/>
						</v-col>
						<v-col cols="4">
							<v-text-field
								v-model="dateFilter"
								variant="outlined"
								:label="$gettext('Search by date')"
								single-line
								:rules="[v => !v || /^(0[1-9]|[12][0-9]|3[01])[-](0[1-9]|1[012])[-]((19|20)\d{2})$/.test(v) || $gettext('Please fill in a valid date as DD-MM-YYYY')]"
								@keyup.enter="updateFilter"
							/>
						</v-col>
						<v-col
							cols="auto"
							class="d-flex align-center"
						>
							<v-btn
								color="primary"
								@click="updateFilter"
							>
								<translate>Search</translate>
							</v-btn>
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<model-backend-table
								v-model="items"
								v-model:option="options"
								:sort-by="[{key: sortBy, order: sortDesc}]"
								endpoint="/api/v0/invoices"
								:filter="filter"
								:headers="tableHeaders"
								:server-items-length="totalItems"
								:items-per-page="25"
								:items-per-page-options="[25, 50, 100]"
							>
								<template #item.identifier="{ item }">
									<a :href="`/bo/invoices/view?id=${item.id}`">
										{{ item.identifier }}
									</a>
								</template>
								<template #item.orderkey="{ item }">
									<a :href="`/bo/orders/view?id=${item.orderId}`">
										{{ item.orderkey }}
									</a>
								</template>
								<template #item.customer="{ item }">
									<a :href="`/bo/users/view?id=${item.userId}`">
										{{ item.customer }}
									</a>
								</template>
								<template #item.amount="{ item }">
									<span
										v-if="item.total < 0"
										class="negative-currency"
									>
										€ {{ item.amount }}
									</span>
									<span v-else>
										€ {{ item.amount }}
									</span>
								</template>
								<template #item.type="{ item }">
									<img
										v-if="item.type === 'Manual'"
										:title="$gettext(item.type)"
										src="/bo/images/icons/user.png"
									>
									<img
										v-else
										:title="$gettext(item.type)"
										src="/bo/images/icons/computer.png"
									>
								</template>
								<template #item.status="{ item }">
									<img
										v-if="item.status === 'due_not_paid'"
										:title="$gettext(invoiceStatuses['not_paid'].message)"
										:src="invoiceStatuses['not_paid'].image"
									>
									<img
										v-if="item.status === 'processing'"
										:title="$gettextInterpolate(invoiceStatuses[item.status].message, {date: item.automatedProcessingDate})"
										:src="invoiceStatuses[item.status].image"
									>
									<img
										v-else
										:title="$gettext(invoiceStatuses[item.status].message)"
										:src="invoiceStatuses[item.status].image"
									>
								</template>
							</model-backend-table>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-col>
	</v-row>
</template>

<script>

export default {
	name: 'InvoicesOverview',

	data () {
		return {
			loading: true,
			items: [],
			identifierFilter: '',
			dateFilter: '',
			filter: {
			},
			options: {
			},
			totalItems: 0,
			sortBy: 'created_at',
			sortDesc: true,
			invoiceStatuses:
				{
					'processing_at_bank': {
						message: 'Debt collection is processed',
						image: '/bo/images/icons/hourglass.png'
					},
					'paid_not_received': {
						message: 'Invoice paid, not received',
						image: '/bo/images/icons/tick.png'
					},
					'paid_intra_invoice': {
						message: 'Paid intra-invoice',
						image: '/bo/images/icons/arrow_undo.png'
					},
					'refunded': {
						message: 'Refunded invoice',
						image: '/bo/images/icons/chart_bar_link.png'
					},
					'paid': {
						message: 'Invoice paid',
						image: '/bo/images/icons/tick.png'
					},

					'not_paid': {
						message: 'Invoice not yet paid',
						image: '/bo/images/icons/cross.png'
					},
					'due_not_paid': {
						message: 'Payment term expired and invoice not yet paid',
						image: '/bo/images/icons/error.png'
					},
				},
		};
	},

	computed: {
		tableHeaders () {
			return [
				{
					title: this.$gettext('Invoice Number'), value: 'identifier', sortable: false, filterable: true,
					filter: (value) => {
						return this.identifierFilter.length === 0 || this.identifierFilter.includes(value);
					}
				},
				{
					title: this.$gettext('Date'), value: 'createdAt', sortable: false, filterable: true,
					filter: (value) => {
						return this.dateFilter.length === 0 || this.dateFilter.includes(value);
					}
				},
				{
					title: this.$gettext('Order'), value: 'orderkey', sortable: false, filterable: false,
				},
				{
					title: this.$gettext('Customer'), value: 'customer', sortable: false, filterable: false,
				},
				{
					title: this.$gettext('Total (including tax)'), value: 'amount', sortable: false, filterable: false,
				},
				{
					title: this.$gettext('Type'), value: 'type', sortable: false, filterable: false,
				},
				{
					title: this.$gettext('Status'), value: 'status', sortable: false, filterable: false,
				},
			];
		},
	},

	methods: {
		/**
		 *
		 */
		updateFilter () {
			if (this.validateDate()) {
				this.filter = {
					'identifier': this.identifierFilter,
					'createdAt': this.dateFilter,
				};
			}
		},

		/**
		 * @returns {boolean}
		 */
		validateDate () {
			return !this.dateFilter || /^(0[1-9]|[12][0-9]|3[01])[/-](0[1-9]|1[012])[/-]((19|20)\d{2})$/.test(this.dateFilter);
		}
	}
};
</script>

